var _excluded = ["htmlProps"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import * as React from 'react';
import { forwardRef, omitThemingProps, StylesProvider, tqc, useMultiStyleConfig, useStyles } from '../../system';
import { createContext } from '../../utils';
import { useNumberInput } from './useNumberInput';
/**
 * React context used to communicate between components
 */
var [NumberInputProvider, useNumberInputContext] = createContext({
  name: 'NumberInputContext',
  errorMessage: "useNumberInputContext: `context` is undefined. Seems you forgot to wrap number-input's components within <NumberInput />"
});
/**
 * NumberInput
 *
 * React component that provides context and logic to all
 * number input sub-components.
 *
 * It renders a `div` by default.
 *
 */
export var NumberInput = forwardRef((props, ref) => {
  var styles = useMultiStyleConfig('NumberInput', props);
  var ownProps = omitThemingProps(props);
  var _useNumberInput = useNumberInput(ownProps),
    {
      htmlProps
    } = _useNumberInput,
    context = _objectWithoutProperties(_useNumberInput, _excluded);
  var ctx = React.useMemo(() => context, [context]);
  return /*#__PURE__*/React.createElement(NumberInputProvider, {
    value: ctx
  }, /*#__PURE__*/React.createElement(StylesProvider, {
    value: styles
  }, /*#__PURE__*/React.createElement(tqc.div, _extends({
    ref: ref
  }, htmlProps, {
    __css: _objectSpread({
      position: 'relative',
      zIndex: 0
    }, styles.root)
  }))));
});
NumberInput.displayName = 'NumberInput';
/**
 * NumberInputStepper
 *
 * React component used to group the increment and decrement
 * button spinners.
 *
 * It renders a `div` by default.
 *
 */
export var NumberInputStepper = forwardRef((props, ref) => {
  var styles = useStyles();
  return /*#__PURE__*/React.createElement(tqc.div, _extends({
    "aria-hidden": true,
    ref: ref
  }, props, {
    __css: _objectSpread({
      display: 'flex',
      flexDirection: 'column',
      position: 'absolute',
      top: '0',
      insetEnd: '0px',
      margin: '1px',
      height: 'calc(100% - 2px)',
      zIndex: 1
    }, styles.stepperGroup)
  }));
});
NumberInputStepper.displayName = 'NumberInputStepper';
/**
 * NumberInputField
 *
 * React component that represents the actual `input` field
 * where users can type to edit numeric values.
 *
 * It renders an `input` by default and ensures only numeric
 * values can be typed.
 *
 */
export var NumberInputField = forwardRef((props, ref) => {
  var {
    getInputProps
  } = useNumberInputContext();
  var input = getInputProps(props, ref);
  var styles = useStyles();
  return /*#__PURE__*/React.createElement(tqc.input, _extends({}, input, {
    __css: _objectSpread({
      width: '100%'
    }, styles.field)
  }));
});
NumberInputField.displayName = 'NumberInputField';
export var StyledStepper = tqc('div', {
  baseStyle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    transition: 'all 0.3s',
    userSelect: 'none',
    cursor: 'pointer',
    lineHeight: 'normal'
  }
});
/**
 * NumberIncrementStepper
 *
 * React component used to increment the number input's value
 *
 * It renders a `div` with `role=button` by default
 */
export var NumberIncrementStepper = forwardRef((props, ref) => {
  var _props$children;
  var {
    getIncrementButtonProps
  } = useNumberInputContext();
  var increment = getIncrementButtonProps(props, ref);
  var styles = useStyles();
  return /*#__PURE__*/React.createElement(StyledStepper, _extends({}, increment, {
    __css: styles.stepper
  }), (_props$children = props.children) !== null && _props$children !== void 0 ? _props$children : /*#__PURE__*/React.createElement(tqc.div, {
    __css: styles.incrementIcon
  }));
});
NumberIncrementStepper.displayName = 'NumberIncrementStepper';
/**
 * NumberDecrementStepper
 *
 * React component used to decrement the number input's value
 *
 * It renders a `div` with `role=button` by default
 */
export var NumberDecrementStepper = forwardRef((props, ref) => {
  var _props$children2;
  var styles = useStyles();
  var {
    getDecrementButtonProps
  } = useNumberInputContext();
  var decrement = getDecrementButtonProps(props, ref);
  return /*#__PURE__*/React.createElement(StyledStepper, _extends({}, decrement, {
    __css: styles.stepper
  }), (_props$children2 = props.children) !== null && _props$children2 !== void 0 ? _props$children2 : /*#__PURE__*/React.createElement(tqc.div, {
    __css: styles.decrementIcon
  }));
});
NumberDecrementStepper.displayName = 'NumberDecrementStepper';